import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Clock, User, Star, BookOpen, Target, ArrowRight } from "lucide-react";
import ProgressJourney from "../../../components/ProgressJourney";
import { COLORS, HEADING_COLOR } from "../../../constants/colors";

const ExistingUserNoQuizHome = () => {
  const navigate = useNavigate();
  const { firstName } = useSelector((state) => state.auth);
  const { targetJob } = useSelector((state) => state.user);

  const assessmentOptions = [
    {
      title: "Target Career Assessment",
      icon: <Target className="w-6 h-6" />,
      details: [
        { text: "6 min", icon: <Clock className="w-3 h-3" />, color: "text-gray-600" },
        { text: "Career Growth", icon: <Star className="w-3 h-3" />, color: "text-blue-600" }
      ],
      description: "Assess your AI knowledge for your newly selected target career and identify the skills needed to successfully transition to your dream role.",
      buttonText: `Assess for '${targetJob || "Selected Career"}'`,
      buttonIcon: <Target className="w-3 h-3" />,
      onClick: () => navigate("/dashboard/career-assessment/ai-literacy-assessment")
    },
    {
      title: "Skip Assessment",
      icon: <ArrowRight className="w-6 h-6" />,
      details: [
        { text: "Basic Plan", icon: <Star className="w-3 h-3" />, color: "text-orange-600" }
      ],
      description: "Generate a basic upskilling plan without assessment. Results in a less personalized learning pathway compared to taking the AI Literacy Assessment.",
      buttonText: "Generate Basic Upskilling Plan",
      buttonIcon: <ArrowRight className="w-3 h-3" />,
      onClick: () => navigate("/dashboard/skills-and-learning/upskilling-pathway-config"),
      warning: true
    }
  ];

  return (
    <div className="min-h-screen py-4 sm:py-6 md:py-8 px-3 sm:px-4 md:px-6">
      <div className="max-w-7xl mx-auto space-y-6 sm:space-y-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-xl sm:text-2xl md:text-2xl font-bold px-2" style={{ color: HEADING_COLOR }}>
            Hi {firstName || "Alex"}, Let's Assess Your AI Literacy
          </h1>
          <p className="text-gray-500 mt-2 text-sm sm:text-base max-w-2xl mx-auto px-2">
            Take our comprehensive AI literacy assessment to unlock personalized learning pathways and accelerate your career growth in the AI era.
          </p>
        </div>

        {/* Your Journey Section */}
        <ProgressJourney currentStep={4} totalSteps={6} />

        {/* Assessment Options */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          {assessmentOptions.map((option, index) => (
            <div key={index} className="bg-white border border-gray-400 rounded-xl p-4 sm:p-6 shadow-sm hover:shadow-md transition-shadow flex flex-col">
              {/* Card Header */}
              <div className="flex items-start gap-3 mb-3">
                <div className="w-8 h-8 sm:w-9 sm:h-9 mt-1 bg-purple-600 rounded-lg flex items-center justify-center text-white flex-shrink-0">
                  {React.cloneElement(option.icon, { 
                    className: "w-4 h-4 sm:w-6 sm:h-6" 
                  })}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start sm:items-center justify-between mb-1 gap-2">
                    <h3 className="font-bold text-gray-900 text-sm sm:text-base leading-tight">{option.title}</h3>
                    {option.warning && (
                      <span className="inline-block px-2 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-full flex-shrink-0">
                        Not Advisable
                      </span>
                    )}
                  </div>
                  {/* Card Details - Below title, next to icon */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mt-2">
                    {option.details.map((detail, detailIndex) => (
                      <div key={detailIndex} className="flex text-sm items-center gap-2">
                        <div className={`${detail.color}`}>
                          {React.cloneElement(detail.icon, { 
                            className: "w-3 h-3" 
                          })}
                        </div>
                        <span className="text-xs font-bold text-gray-500">{detail.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Card Description */}
              <p className="text-gray-500 text-xs sm:text-sm leading-relaxed mb-4 flex-1">
                {option.description}
              </p>

              {/* Action Button */}
              <div className="mt-auto">
                <button
                  onClick={option.onClick}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm rounded-lg font-medium transition-colors flex items-center justify-center gap-2 bg-white text-black border border-gray-400 hover:bg-gray-50"
                >
                  {React.cloneElement(option.buttonIcon, { 
                    className: "w-3 h-3" 
                  })}
                  <span className="leading-tight">{option.buttonText}</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ExistingUserNoQuizHome; 